import { useCallback, useEffect, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Globe, Users, MousePointer2, FileText, Timer, LogOut, Layers,
  TrendingUp, TrendingDown, Download, Mail, ClipboardCheck, Info, MapPin,
  Radio, Megaphone, Filter,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

/* Brand palette (charcoal + gold editorial) */
const C = {
  gold: "#B58A45", walnut: "#4B3B31", taupe: "#B6A895", charcoal: "#202020",
  mist: "#E9E4DC", green: "#9CAE92", rose: "#CDA6A0",
};
const CHANNEL_COLOR = { search: C.gold, social: C.walnut, direct: C.taupe, referral: C.green };
const DEVICE_COLOR = { mobile: C.gold, desktop: C.walnut, tablet: C.taupe };
const PIE_COLORS = [C.gold, C.walnut, C.taupe, C.green, C.rose, C.charcoal];

const RANGES = [
  { key: "7", label: "7 days" },
  { key: "30", label: "30 days" },
  { key: "90", label: "90 days" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom" },
];

const fmtNum = (n) => {
  if (n == null) return "—";
  const a = Math.abs(n);
  if (a >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (a >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return `${n}`;
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return iso; } };
const fmtDuration = (sec) => {
  const s = Math.max(0, Math.round(sec || 0));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const Toggle = ({ options, value, onChange, testidPrefix }) => (
  <div className="inline-flex flex-wrap gap-1 rounded-xl bg-secondary/60 p-1">
    {options.map((o) => (
      <button
        key={o.key}
        onClick={() => onChange(o.key)}
        data-testid={`${testidPrefix}-${o.key}`}
        className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${value === o.key ? "bg-[hsl(var(--gold))] text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const Delta = ({ v, unit = "%", invert = false, testid }) => {
  if (v == null) return null;
  const good = invert ? v <= 0 : v >= 0;
  const Icon = v >= 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`mt-1 inline-flex items-center gap-1 text-xs ${good ? "text-[hsl(var(--gold))]" : "text-destructive"}`} data-testid={testid}>
      <Icon size={12} />{v >= 0 ? "+" : ""}{v}{unit}<span className="text-muted-foreground"> vs prev</span>
    </span>
  );
};

const Kpi = ({ label, value, icon: Icon, sub, delta, testid }) => (
  <Card className="p-5 border-border shadow-[var(--shadow-sm)]" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
      {Icon && <Icon size={16} className="text-[hsl(var(--gold))]" />}
    </div>
    <p className="mt-3 font-display text-3xl leading-none tabular-nums">{value}</p>
    {sub && <p className="mt-2 text-xs text-muted-foreground">{sub}</p>}
    {delta ? <div>{delta}</div> : null}
  </Card>
);

const ChartCard = ({ title, subtitle, children, right }) => (
  <Card className="p-5 border-border shadow-[var(--shadow-sm)]">
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h4 className="font-display text-lg">{title}</h4>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </Card>
);

const tooltipStyle = {
  borderRadius: 12,
  border: "1px solid rgba(32,32,32,0.10)",
  background: "#FAF8F3",
  fontSize: 12,
  boxShadow: "0 8px 24px rgba(32,32,32,0.10)",
};

const ShareBars = ({ rows, color, testid, labelFmt = (x) => x, empty = "No data yet." }) => {
  if (!rows || rows.length === 0) return <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p>;
  return (
    <div className="space-y-3" data-testid={testid}>
      {rows.map((r) => (
        <div key={r.key} className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="truncate text-foreground">{labelFmt(r.key)}</span>
            <span className="tabular-nums text-muted-foreground">{fmtNum(r.value)} · {r.share}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-secondary/70">
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, r.share)}%`, background: color || C.gold }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const RealtimePanel = ({ rt }) => {
  const minutes = rt?.minutes || [];
  const maxV = Math.max(1, ...minutes.map((m) => m.pageviews));
  const active = rt?.active_visitors ?? 0;
  return (
    <Card className="border-border p-5 shadow-[var(--shadow-sm)]" data-testid="bi-web-realtime">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="relative flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--gold))] opacity-60" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-[hsl(var(--gold))]" />
          </span>
          <div>
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
              <Radio size={13} className="text-[hsl(var(--gold))]" />Right now
            </div>
            <p className="font-display text-3xl leading-none tabular-nums" data-testid="bi-web-realtime-active">
              {active} <span className="text-lg text-muted-foreground">{active === 1 ? "visitor" : "visitors"}</span>
            </p>
            <p className="mt-1 text-xs text-muted-foreground">active in the last 5 minutes</p>
          </div>
        </div>

        {/* 30-min sparkline */}
        <div className="flex-1 min-w-[200px]">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Pageviews · last 30 min</span>
            <span className="tabular-nums">{rt?.pageviews_30m ?? 0}</span>
          </div>
          <div className="flex h-12 items-end gap-[2px]" aria-hidden>
            {minutes.map((m, i) => (
              <div key={i} className="flex-1 rounded-t-[2px] bg-[hsl(var(--gold)/0.55)] transition-[height]"
                style={{ height: `${Math.max(6, (m.pageviews / maxV) * 100)}%` }} />
            ))}
          </div>
        </div>

        {/* active pages */}
        <div className="min-w-[180px]">
          <p className="mb-1 text-xs text-muted-foreground">Active pages</p>
          {(rt?.active_pages || []).length ? (
            <ul className="space-y-1 text-sm">
              {rt.active_pages.slice(0, 4).map((p) => (
                <li key={p.key} className="flex items-center justify-between gap-3">
                  <span className="truncate text-foreground">{p.key}</span>
                  <span className="tabular-nums text-muted-foreground">{p.value}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No active pages.</p>
          )}
        </div>
      </div>
    </Card>
  );
};

const FunnelView = ({ steps }) => {
  if (!steps || steps.length === 0) return null;
  const top = steps[0]?.sessions || 0;
  return (
    <div className="space-y-4" data-testid="bi-web-funnel">
      {steps.map((st, i) => {
        const width = top ? Math.max(4, Math.round((st.sessions / top) * 100)) : 0;
        return (
          <div key={st.step}>
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-foreground">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--gold)/0.15)] text-[11px] font-semibold text-[hsl(var(--gold))]">{i + 1}</span>
                {st.step}
              </span>
              <span className="tabular-nums text-muted-foreground">
                {fmtNum(st.sessions)} · {st.overall_pct}%
              </span>
            </div>
            <div className="h-9 overflow-hidden rounded-lg bg-secondary/60">
              <div className="flex h-full items-center rounded-lg bg-[hsl(var(--gold))] px-3 text-xs font-medium text-primary-foreground"
                style={{ width: `${width}%` }}>
                {width > 12 ? fmtNum(st.sessions) : ""}
              </div>
            </div>
            {i > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                {st.step_pct}% continued from previous · {fmtNum(st.dropoff)} dropped off
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CampaignsTable = ({ rows }) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="py-8 text-center" data-testid="bi-web-campaigns-empty">
        <p className="text-sm text-muted-foreground">No campaign traffic yet.</p>
        <p className="mt-1 text-xs text-muted-foreground">Tag your links with <code className="rounded bg-secondary px-1">?utm_campaign=</code> to see them here.</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="bi-web-campaigns">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-[0.08em] text-muted-foreground">
            <th className="pb-2 pr-3 font-medium">Campaign</th>
            <th className="pb-2 pr-3 font-medium">Source / Medium</th>
            <th className="pb-2 pr-3 text-right font-medium">Sessions</th>
            <th className="pb-2 pr-3 text-right font-medium">Conv.</th>
            <th className="pb-2 text-right font-medium">Rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.campaign}-${i}`} className="border-b border-border/60 last:border-0">
              <td className="py-2.5 pr-3 font-medium text-foreground">{r.campaign}</td>
              <td className="py-2.5 pr-3 text-muted-foreground">
                {r.source || "—"}{r.medium ? ` / ${r.medium}` : ""}
              </td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{fmtNum(r.sessions)}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{fmtNum(r.conversions)}</td>
              <td className="py-2.5 text-right tabular-nums text-[hsl(var(--gold))]">{r.conversion_rate}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const BIWeb = () => {
  const [range, setRange] = useState("all");
  const [customStart, setCustomStart] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().slice(0, 10); });
  const [customEnd, setCustomEnd] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const queryParams = useCallback(() => {
    const p = { range };
    if (range === "custom") { p.start = customStart; p.end = customEnd; }
    return p;
  }, [range, customStart, customEnd]);

  const load = useCallback(() => {
    if (range === "custom" && (!customStart || !customEnd)) return;
    setLoading(true);
    api.get("/superadmin/bi/web", { params: queryParams() })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load website analytics."))
      .finally(() => setLoading(false));
  }, [range, customStart, customEnd, queryParams]);
  useEffect(() => { load(); }, [load]);

  // Realtime: poll every 15s (range-independent).
  const [realtime, setRealtime] = useState(null);
  useEffect(() => {
    let alive = true;
    const tick = () => api.get("/superadmin/bi/web/realtime")
      .then(({ data }) => { if (alive) setRealtime(data); })
      .catch(() => {});
    tick();
    const id = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/superadmin/bi/web/export.csv", { params: queryParams(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `website_${range}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  const k = data?.kpis;
  const d = data?.comparison?.has_comparison ? data.comparison.deltas : null;
  const nvr = k ? [
    { name: "New", value: k.new_visitors },
    { name: "Returning", value: k.returning_visitors },
  ] : [];

  return (
    <div className="space-y-6" data-testid="bi-web">
      {/* Header + controls */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Globe size={20} className="text-[hsl(var(--gold))]" />
            <h2 className="font-display text-2xl">Website</h2>
            {data?.has_real_data ? (
              <Badge variant="secondary" className="border border-[hsl(var(--gold))] text-[hsl(var(--gold))]" data-testid="bi-web-badge">Live data</Badge>
            ) : (
              <Badge variant="secondary" className="text-muted-foreground" data-testid="bi-web-badge">Sample data</Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">First-party analytics for intrinsicdaily.com — traffic, engagement, sources, and conversions.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-xl" onClick={exportCsv} data-testid="bi-web-export"><Download size={15} className="mr-1.5" />Export CSV</Button>
        </div>
      </div>

      {realtime && <RealtimePanel rt={realtime} />}

      <div className="flex flex-wrap items-center gap-3">
        <Toggle options={RANGES} value={range} onChange={setRange} testidPrefix="bi-web-range" />
      </div>

      {range === "custom" && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-secondary/30 p-3" data-testid="bi-web-custom-range">
          <label className="space-y-1"><span className="text-xs text-muted-foreground">From</span>
            <Input type="date" value={customStart} max={customEnd} onChange={(e) => setCustomStart(e.target.value)} className="h-9 rounded-lg" data-testid="bi-web-custom-start" />
          </label>
          <label className="space-y-1"><span className="text-xs text-muted-foreground">To</span>
            <Input type="date" value={customEnd} min={customStart} max={todayISO()} onChange={(e) => setCustomEnd(e.target.value)} className="h-9 rounded-lg" data-testid="bi-web-custom-end" />
          </label>
          {data?.window && <span className="pb-2 text-xs text-muted-foreground">Comparing to {data.previous_window?.start} → {data.previous_window?.end}</span>}
        </div>
      )}

      {data && !data.has_real_data && (
        <Card className="flex flex-wrap items-center gap-3 border-[color:rgba(181,138,69,0.35)] bg-[hsl(var(--gold)/0.06)] p-4" data-testid="bi-web-sample-banner">
          <Info size={18} className="text-[hsl(var(--gold))]" />
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">You're viewing sample data.</span> The on-site tracker is live — as soon as real visitors arrive on intrinsicdaily.com, this dashboard switches to your live numbers automatically.
          </p>
        </Card>
      )}

      {loading && !data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-[var(--radius)] bg-secondary/60" />)}
        </div>
      ) : k ? (
        <>
          {/* KPI cards — row 1 */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi testid="bi-web-kpi-visitors" label="Visitors" value={fmtNum(k.visitors)} icon={Users}
              sub={`${fmtNum(k.new_visitors)} new · ${fmtNum(k.returning_visitors)} returning`}
              delta={d && <Delta v={d.visitors} testid="bi-web-delta-visitors" />} />
            <Kpi testid="bi-web-kpi-sessions" label="Sessions" value={fmtNum(k.sessions)} icon={Layers}
              sub={`${k.pages_per_session} pages / session`}
              delta={d && <Delta v={d.sessions} testid="bi-web-delta-sessions" />} />
            <Kpi testid="bi-web-kpi-pageviews" label="Pageviews" value={fmtNum(k.pageviews)} icon={FileText}
              delta={d && <Delta v={d.pageviews} testid="bi-web-delta-pageviews" />} />
            <Kpi testid="bi-web-kpi-avg" label="Avg. Session" value={fmtDuration(k.avg_session_sec)} icon={Timer}
              sub="time on site"
              delta={d && <Delta v={d.avg_session_sec} testid="bi-web-delta-avg" />} />
          </div>

          {/* KPI cards — row 2 */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi testid="bi-web-kpi-bounce" label="Bounce Rate" value={`${k.bounce_rate}%`} icon={LogOut}
              sub="single-page sessions"
              delta={d && <Delta v={d.bounce_rate} unit=" pp" invert testid="bi-web-delta-bounce" />} />
            <Kpi testid="bi-web-kpi-pps" label="Pages / Session" value={k.pages_per_session} icon={MousePointer2}
              delta={d && <Delta v={d.pages_per_session} testid="bi-web-delta-pps" />} />
            <Kpi testid="bi-web-kpi-conversions" label="Conversions" value={fmtNum(k.conversions)} icon={ClipboardCheck}
              sub={`${k.conversion_rate}% of sessions`}
              delta={d && <Delta v={d.conversions} testid="bi-web-delta-conversions" />} />
            <Kpi testid="bi-web-kpi-new" label="New Visitors" value={`${k.new_pct}%`} icon={TrendingUp}
              sub={`${fmtNum(k.new_visitors)} first-time`} />
          </div>

          {/* Traffic over time */}
          <ChartCard title="Traffic over time" subtitle="Visitors, sessions & pageviews per day">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data.series} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.gold} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={C.gold} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gPv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.walnut} stopOpacity={0.22} />
                    <stop offset="100%" stopColor={C.walnut} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(32,32,32,0.06)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: "#6b6459" }} minTickGap={28} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#6b6459" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDate} />
                <Area type="monotone" dataKey="pageviews" name="Pageviews" stroke={C.walnut} strokeWidth={1.5} fill="url(#gPv)" />
                <Area type="monotone" dataKey="visitors" name="Visitors" stroke={C.gold} strokeWidth={2} fill="url(#gVisitors)" />
                <Area type="monotone" dataKey="sessions" name="Sessions" stroke={C.taupe} strokeWidth={1.5} fillOpacity={0} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Funnel + Acquisition */}
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard title="Conversion funnel" subtitle="Home → Sunday Reset → sign-up" right={<Filter size={16} className="text-[hsl(var(--gold))]" />}>
              <FunnelView steps={data.funnel} />
            </ChartCard>
            <ChartCard title="Acquisition channels" subtitle="Sessions by source">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.channels} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(32,32,32,0.06)" vertical={false} />
                  <XAxis dataKey="key" tickFormatter={titleCase} tick={{ fontSize: 12, fill: "#6b6459" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b6459" }} axisLine={false} tickLine={false} width={40} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={titleCase} cursor={{ fill: "rgba(181,138,69,0.06)" }} />
                  <Bar dataKey="value" name="Sessions" radius={[8, 8, 0, 0]}>
                    {(data.channels || []).map((c) => <Cell key={c.key} fill={CHANNEL_COLOR[c.key] || C.gold} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Campaigns + New/Returning */}
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard title="Campaigns" subtitle="UTM-tagged links & posts" right={<Megaphone size={16} className="text-[hsl(var(--gold))]" />}>
              <CampaignsTable rows={data.campaigns} />
            </ChartCard>
            <ChartCard title="New vs returning" subtitle="Visitor loyalty in this period">
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="55%" height={220}>
                  <PieChart>
                    <Pie data={nvr} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82} paddingAngle={2}>
                      <Cell fill={C.gold} />
                      <Cell fill={C.taupe} />
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm"><span className="h-2.5 w-2.5 rounded-full" style={{ background: C.gold }} />New</div>
                    <p className="font-display text-2xl tabular-nums">{fmtNum(k.new_visitors)}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-sm"><span className="h-2.5 w-2.5 rounded-full" style={{ background: C.taupe }} />Returning</div>
                    <p className="font-display text-2xl tabular-nums">{fmtNum(k.returning_visitors)}</p>
                  </div>
                </div>
              </div>
            </ChartCard>
          </div>

          {/* Devices + Countries */}
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard title="Devices" subtitle="Sessions by device type">
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="55%" height={220}>
                  <PieChart>
                    <Pie data={data.devices} dataKey="value" nameKey="key" innerRadius={52} outerRadius={82} paddingAngle={2}>
                      {(data.devices || []).map((x, i) => <Cell key={x.key} fill={DEVICE_COLOR[x.key] || PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtNum(v), titleCase(n)]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {(data.devices || []).map((x) => (
                    <div key={x.key} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: DEVICE_COLOR[x.key] || C.gold }} />{titleCase(x.key)}</span>
                      <span className="tabular-nums text-muted-foreground">{x.share}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </ChartCard>

            <ChartCard title="Top countries" subtitle="Where your visitors are" right={<MapPin size={16} className="text-[hsl(var(--gold))]" />}>
              <ShareBars rows={data.countries} color={C.walnut} testid="bi-web-countries" empty="No location data yet." />
            </ChartCard>
          </div>

          {/* Top pages + Referrers */}
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard title="Top pages" subtitle="Most-viewed pages">
              <ShareBars rows={data.top_pages} color={C.gold} testid="bi-web-top-pages" empty="No pageviews yet." />
            </ChartCard>
            <ChartCard title="Top referrers" subtitle="External sites sending traffic">
              <ShareBars rows={data.top_referrers} color={C.taupe} testid="bi-web-referrers" empty="No referral traffic yet." />
            </ChartCard>
          </div>

          {/* Conversions */}
          <ChartCard title="Conversions" subtitle="Sign-ups & completions from the site">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl border border-border bg-secondary/30 p-4" data-testid="bi-web-conv-newsletter">
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><Mail size={15} className="text-[hsl(var(--gold))]" />Newsletter sign-ups</div>
                <p className="mt-2 font-display text-3xl tabular-nums">{fmtNum(k.newsletter_signups)}</p>
              </div>
              <div className="rounded-xl border border-border bg-secondary/30 p-4" data-testid="bi-web-conv-audit">
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><ClipboardCheck size={15} className="text-[hsl(var(--gold))]" />Life Audit completions</div>
                <p className="mt-2 font-display text-3xl tabular-nums">{fmtNum(k.audit_completions)}</p>
              </div>
              <div className="rounded-xl border border-border bg-secondary/30 p-4" data-testid="bi-web-conv-rate">
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp size={15} className="text-[hsl(var(--gold))]" />Conversion rate</div>
                <p className="mt-2 font-display text-3xl tabular-nums">{k.conversion_rate}%</p>
              </div>
            </div>
          </ChartCard>
        </>
      ) : (
        <Card className="p-10 text-center" data-testid="bi-web-empty">
          <p className="text-sm text-muted-foreground">No analytics available.</p>
        </Card>
      )}
    </div>
  );
};
